/**
 * Signed in with no organisation left (16 Oct 2026, G2 / G3): every membership is paused, or they
 * were removed. Before, the app opened empty (no switcher, no setup) — or, for a removed member, the
 * server quietly made them a new organisation and sent them to setup.
 *
 *   paused   “Your access to X is paused” — Ask for access (the admins' bell), per organisation
 *   removed  “You're no longer in X”
 *   waiting  “Waiting for X to approve you” — they used an organisation code without an invitation (15)
 *   then     Join with a code · Create my own organisation · Sign out
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useClerk } from '@clerk/clerk-react'
import { T } from './orgTheme'
import { useOrgs } from '@/lib/useOrgs'
import { orgApi } from '@/lib/orgApi'
import { Shell, Tile } from '@/components/org/JoinCard'
import { joinBtnPrimary as primary, joinBtnSecondary as secondary } from '@/components/org/orgHelpers'

export function NoAccess() {
  const orgs = useOrgs()
  const navigate = useNavigate()
  const { signOut } = useClerk()
  const [asked, setAsked] = useState<Record<string, 'busy' | 'sent'>>({})
  const [err, setErr] = useState('')
  const [coding, setCoding] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const paused = orgs.paused, removed = orgs.removed.filter(r => !paused.some(p => p.id === r.id))
  const waiting = orgs.requested
  const one = paused[0] ?? removed[0]
  const [sentTo, setSentTo] = useState<string | null>(null)

  // Waiting for an answer (20 Oct 2026 — the Check again button went): look quietly once a
  // minute and whenever the tab comes back, and load properly only once something changed, so an
  // approved person moves in by themselves. bootstrap makes nothing while a request waits.
  const waitingKey = waiting.map(w => w.id).join()
  useEffect(() => {
    if (!waitingKey) return
    let off = false
    const look = async () => {
      try {
        const r = await orgApi.bootstrap({})
        if (!off && (r.orgs.length > 0 || (r.requested ?? []).map(w => w.id).join() !== waitingKey)) void orgs.reload()
      } catch { /* try again next time */ }
    }
    const t = setInterval(look, 60_000)
    const onVisible = () => { if (document.visibilityState === 'visible') void look() }
    document.addEventListener('visibilitychange', onVisible)
    return () => { off = true; clearInterval(t); document.removeEventListener('visibilitychange', onVisible) }
  }, [waitingKey]) // eslint-disable-line react-hooks/exhaustive-deps -- orgs.reload is stable enough; re-arm only when the wait changes

  const ask = async (orgId: string) => {
    setErr(''); setAsked(a => ({ ...a, [orgId]: 'busy' }))
    try {
      const r = await orgApi.requestAccess(orgId)
      // Already turned back on (an admin did it while this page was open): straight in.
      if (r.active) { await orgs.reload(); navigate('/', { replace: true }); return }
      setAsked(a => ({ ...a, [orgId]: 'sent' }))
    }
    catch (e) { setErr((e as Error).message); setAsked(a => { const { [orgId]: _x, ...rest } = a; void _x; return rest }) }
  }
  const join = async () => {
    setBusy(true); setErr('')
    try {
      const j = await orgs.joinWithCode(code)
      if (j.requested) { setSentTo(j.org.name); setCoding(false); setCode('') } else navigate('/', { replace: true })
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }
  const createOwn = async () => {
    setBusy(true); setErr('')
    try { await orgs.createOwnOrg(); navigate('/', { replace: true }) } catch (e) { setErr((e as Error).message); setBusy(false) }
  }

  // Only a request is waiting — someone new who used a code without an invitation.
  if (!paused.length && !removed.length) {
    return (
      <Shell>
        <Tile initials="…" bg={T.tintSoft} fg={T.purple} />
        <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-.01em', color: T.dark }}>
          {waiting.length === 1 ? `Waiting for ${waiting[0].name} to approve you` : 'Waiting for approval'}
        </span>
        <span style={{ fontSize: 14, color: T.ink, lineHeight: 1.5 }}>
          {waiting.length === 1 ? 'Your request to join was sent to its admins.' : `Your requests to join ${waiting.map(w => w.name).join(', ')} were sent to their admins.`}
          {' '}You’ll get an email as soon as they answer. In the meantime you can create your own organisation.
        </span>
        {waiting.filter(w => w.contactEmail).map(w => (
          <span key={w.id} style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.5 }}>Questions? Contact {w.name} at <a href={`mailto:${w.contactEmail}`} style={{ color: T.purpleText }}>{w.contactEmail}</a>.</span>
        ))}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
          <button onClick={() => void createOwn()} disabled={busy} style={primary}>Create my own organisation</button>
          <button onClick={() => void signOut({ redirectUrl: '/sign-in' })} style={{ ...secondary, border: 'none', background: 'transparent', color: T.muted }}>Sign out</button>
        </div>
        {err && <span role="alert" style={{ fontSize: 13.5, color: T.red }}>{err}</span>}
      </Shell>
    )
  }

  return (
    <Shell>
      <Tile initials={paused.length ? '‖' : '!'} bg={paused.length ? T.amberBg : T.hover} fg={paused.length ? T.amber : T.muted} />
      <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-.01em', color: T.dark }}>
        {paused.length === 1 && !removed.length ? `Your access to ${one.name} is paused`
          : paused.length ? 'Your access is paused'
          : removed.length === 1 ? `You’re no longer in ${one.name}` : 'You’re no longer in your organisations'}
      </span>
      <span style={{ fontSize: 14, color: T.ink, lineHeight: 1.5 }}>
        {paused.length
          ? 'An admin turned it off. Your progress and details are kept — ask for it back, or carry on another way.'
          : 'An admin removed you. You can join an organisation with a code, or create your own.'}
      </span>

      {paused.length > 0 && (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
          {paused.map(p => (
            asked[p.id] === 'sent'
              ? <span key={p.id} style={{ fontSize: 14, color: T.green, padding: '10px 0' }}>Request sent — {p.name}’s admins will see it.</span>
              : <button key={p.id} onClick={() => void ask(p.id)} disabled={asked[p.id] === 'busy'} style={primary}>
                  {asked[p.id] === 'busy' ? 'Sending…' : paused.length > 1 ? `Ask ${p.name} for access` : 'Ask for access'}
                </button>
          ))}
          {removed.length > 0 && <span style={{ fontSize: 13, color: T.muted, lineHeight: 1.5 }}>You were removed from {removed.map(r => r.name).join(', ')}.</span>}
        </div>
      )}

      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8, marginTop: paused.length ? 0 : 6 }}>
        {coding ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={code} autoFocus onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))} placeholder="Organisation code" aria-label="Organisation code"
              onKeyDown={e => { if (e.key === 'Enter' && code.length === 6) void join() }}
              style={{ flex: 1, minWidth: 0, height: 44, borderRadius: 10, border: `1px solid ${T.inputBorder}`, padding: '0 12px', fontFamily: 'ui-monospace, monospace', fontSize: 16, letterSpacing: '.14em' }} />
            <button onClick={() => void join()} disabled={code.length !== 6 || busy} style={{ ...primary, width: 'auto', padding: '0 18px', opacity: code.length === 6 ? 1 : 0.5 }}>{busy ? 'Sending…' : 'Join'}</button>
          </div>
        ) : <button onClick={() => setCoding(true)} style={paused.length ? secondary : primary}>Join with a code</button>}
        <button onClick={() => void createOwn()} disabled={busy} style={secondary}>Create my own organisation</button>
        <button onClick={() => void signOut({ redirectUrl: '/sign-in' })} style={{ ...secondary, border: 'none', background: 'transparent', color: T.muted }}>Sign out</button>
      </div>
      {sentTo && <span role="status" style={{ fontSize: 13.5, color: T.green, lineHeight: 1.5 }}>Request sent to {sentTo}. An admin needs to approve it — you’ll get an email.</span>}
      {err && <span role="alert" style={{ fontSize: 13.5, color: T.red }}>{err}</span>}
    </Shell>
  )
}
